import { getTimezoneOffset, differenceInMinutes, addMinutes } from 'owa-datetime';
import type { OwaDate } from 'owa-datetime';

/**
 * Adjusts the end date for events that cross the DST "fall back" transition.
 *
 * During DST fall back transitions, clocks move back one hour.
 * For example, at 2:00am PDT (UTC-7), the clock resets to 1:00am PST (UTC-8).
 *
 * This creates two separate "1:00–2:00am" blocks on the same calendar day.
 * If an event starts in the first block (PDT) and ends in the second block (PST),
 * the raw comparison can make it look like the event ends before it starts.
 *
 * Example:
 * start = 1:30am PDT (offset -420)
 * end   = 1:00am PST (offset -480)
 * difference = -30 minutes (invalid)
 *
 * This helper detects the overlap and shifts the end time forward
 * by the size of the DST offset change (usually 60 minutes).
 *
 * After adjustment:
 * end = 2:00am PST
 * duration = 90 minutes (valid)
 *
 * Note:
 * - This is a temporary workaround until we fully support rendering
 *   both daylight and standard time intervals in the UI.
 */
export default function adjustEndDateForDSTTransition(
    startDate: OwaDate,
    endDate: OwaDate
): OwaDate {
    const startDateOffset = getTimezoneOffset(startDate);
    const endDateOffset = getTimezoneOffset(endDate);

    // Offset difference (typically 60 minutes during DST transitions)
    const offsetChangeMinutes = Math.abs(endDateOffset - startDateOffset);

    // Actual event duration from start to end
    const eventDuration = differenceInMinutes(endDate, startDate);

    // Detect fall back "overlap":
    // - offset became more negative (PDT -> PST, -7h -> -8h)
    // - duration is negative or very short due to DST transition
    if (startDateOffset < endDateOffset && eventDuration <= offsetChangeMinutes) {
        return addMinutes(endDate, offsetChangeMinutes);
    }

    return endDate;
}
